import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField } from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import { PageHeader, Card, DataTable, ErrorState, UnvButton, UnvModal, Icon } from 'components'
import styles from './SyllabusBuilderPage.module.scss'

const SyllabusApprovalPage = () => {
  const { t } = useTranslation('common')
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(null)
  const [comment, setComment] = useState('')
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/syllabus/', { params: { status: 'pending' } })
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const approve = async (id) => {
    try {
      await $api.post(`/syllabus/${id}/approve/`)
      setNotify({ isOpen: true, message: t('syllabus.approved_msg'), type: 'success', sound: 'success' })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('syllabus.error'), type: 'error', sound: 'error' })
    }
  }

  const reject = async () => {
    if (!rejectOpen) return
    try {
      await $api.post(`/syllabus/${rejectOpen.id}/reject/`, { comment })
      setNotify({ isOpen: true, message: t('syllabus.rejected_msg'), type: 'success', sound: 'success' })
      setRejectOpen(null)
      setComment('')
      load()
    } catch {
      setNotify({ isOpen: true, message: t('syllabus.error'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        icon={<Icon name="syllabus" size={28} />}
        title={t('syllabus.approval_title')}
        subtitle={t('auto.syllabusapproval.subtitle', 'Очередь силлабусов на утверждение')}
      />

      {error ? (
        <ErrorState onRetry={load} />
      ) : (
        <Card padding="md" className={styles.content}>
          <Card.Body>
            <DataTable
              loading={loading}
              data={list}
              columns={[
                { key: 'subject_name', label: t('syllabus.col_subject'), render: (_, r) => r.subject_name || r.title || '—' },
                { key: 'direction_name', label: t('syllabus.col_direction'), render: (_, r) => r.direction_name || r.direction?.name || '—' },
                { key: 'year', label: t('syllabus.col_year') },
                { key: 'version', label: t('syllabus.col_version') },
                { key: 'author_name', label: t('syllabus.col_author'), render: (_, r) => r.author_name || r.author?.full_name || '—' },
                {
                  key: 'actions',
                  label: '',
                  render: (_, row) => (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <UnvButton variant="success" size="sm" iconLeft="check" onClick={() => approve(row.id)}>{t('syllabus.approve_btn')}</UnvButton>
                      <UnvButton variant="danger" size="sm" iconLeft="x" onClick={() => setRejectOpen(row)}>{t('syllabus.reject_btn')}</UnvButton>
                    </div>
                  ),
                },
              ]}
              emptyText={t('syllabus.queue_empty')}
            />
          </Card.Body>
        </Card>
      )}

      <UnvModal
        open={!!rejectOpen}
        onClose={() => setRejectOpen(null)}
        title={t('syllabus.reject_dialog_title')}
        size="sm"
      >
        <TextField
          fullWidth
          label={t('syllabus.comment_label')}
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          margin="normal"
        />
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setRejectOpen(null)}>{t('common.cancel')}</UnvButton>
          <UnvButton variant="danger" iconLeft="x" onClick={reject}>{t('syllabus.reject_btn')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default SyllabusApprovalPage
